import type { BrowserOptions, ErrorEvent, EventHint } from "@sentry/react";
import GrailedAPIError from "storefront/GrailedAPI/Error";
import { fingerprintGrailedAPIError } from "storefront/lib/fingerprintGrailedAPIError";
import { grailedAPIErrorSource } from "storefront/lib/grailedAPIErrorSource";

export const beforeSend = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  const exception = hint?.originalException;
  if (!(exception instanceof GrailedAPIError)) return event;

  const fingerprint = fingerprintGrailedAPIError(exception);
  const { origin, rayId, contentType } = grailedAPIErrorSource(exception);

  return {
    ...event,
    ...(fingerprint ? { fingerprint } : {}),
    tags: { ...event.tags, apiErrorSource: origin },
    contexts: {
      ...event.contexts,
      apiError: { source: origin, cfRay: rayId, contentType },
    },
  };
};

/**
 * The storefront and the legacy client report to separate Sentry projects, but both throw the same
 * GrailedAPIError from the same request layer, so they see the same errors and want the same
 * filtering and enrichment. Spread these into each app's own init, which keeps the settings that
 * genuinely differ — dsn, environment, release, breadcrumb depth. Spread first: a per-app
 * `ignoreErrors` or `denyUrls` replaces the array here rather than extending it.
 */
export const sharedSentryOptions: Pick<
  BrowserOptions,
  "ignoreErrors" | "denyUrls" | "beforeSend"
> = {
  ignoreErrors: [
    /TypeError: t is not a function. \(In 't\(\)', 't' is undefined\)$/,

    // Noise from third-party/extension code calling global logMutedMessage in page context
    /^ReferenceError: Can't find variable: logMutedMessage$/,

    // Injected by Meta's in-app browsers (Instagram, Facebook), unactionable
    /window\.webkit\.messageHandlers/,
    /Error invoking \w+: Java object is gone/,

    // Thrown inside Segment's Bing Ads destination, not our code
    /window\.UET/,

    // Client-side network failures — fetch() never received a response
    // (connection dropped, DNS failure, offline, or server reset mid-request).
    // Server-side issues are caught by platform Sentry, Datadog, and New Relic.
    // The SDK appends the request host to these messages, so allow a suffix.
    /^TypeError: Failed to fetch( \(.+\))?$/,
    /^TypeError: Load failed( \(.+\))?$/,
    /^TypeError: NetworkError when attempting to fetch resource\.( \(.+\))?$/,
  ],
  denyUrls: [
    // Errors inside OneTrust's consent banner SDK are not in our control
    /cdn\.cookielaw\.org/,
    /otBannerSdk\.js/,

    // Browser extensions that patch window.fetch / XMLHttpRequest in the page
    // context, then throw on our analytics requests
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-web-extension:\/\//,
    /\/executors\/\d+\.js$/,
  ],
  beforeSend,
};
